import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import Logo from '../assets/logo/logo.svg';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

function generatePassword() {
  // Simple strong password generator
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%^&*';
  let pass = '';
  for (let i = 0; i < 12; i++) pass += chars[Math.floor(Math.random() * chars.length)];
  return pass;
}

const AcceptInvite = () => {
  const query = useQuery();
  const navigate = useNavigate();
  const token = query.get('token');
  const [loading, setLoading] = useState(true);
  const [valid, setValid] = useState(false);
  const [form, setForm] = useState({
    full_name: '',
    username: '',
    password: '',
    confirm_password: ''
  });
  const [error, setError] = useState('');
  const [usernameAvailable, setUsernameAvailable] = useState(true);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    // Validate token with backend
    const validateToken = async () => {
      try {
        // Remove auth header for this public endpoint
        const res = await axios.get(`/api/users/validate-invite?token=${token}`, {
          headers: {
            Authorization: ''
          }
        });
        setForm(f => ({ ...f, full_name: res.data.full_name || '', username: '' }));
        setValid(true);
      } catch (err) {
        setError('Invalid or expired invite link.');
      } finally {
        setLoading(false);
      }
    };
    if (token) validateToken();
    else {
      setError('Invalid invite link.');
      setLoading(false);
    }
  }, [token]);

  // Username availability check
  const checkUsername = async (username) => {
    if (!username) return;
    setCheckingUsername(true);
    try {
      // Remove auth header for this public endpoint
      const res = await axios.get(`/api/users/check-username?username=${username}`, {
        headers: {
          Authorization: ''
        }
      });
      setUsernameAvailable(res.data.available);
    } catch {
      setUsernameAvailable(true);
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (e.target.name === 'username') {
      checkUsername(e.target.value);
    }
  };

  const handleSuggestPassword = () => {
    const pwd = generatePassword();
    setForm(f => ({ ...f, password: pwd, confirm_password: pwd }));
    toast.success('Suggested password filled!');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (!usernameAvailable) {
      toast.error('Username is already taken');
      return;
    }
    setLoading(true);
    try {
      // Remove auth header for this public endpoint
      await axios.post('/api/users/accept-invite', {
        token,
        username: form.username,
        password: form.password,
        full_name: form.full_name
      }, {
        headers: {
          Authorization: ''
        }
      });
      toast.success('Account activated! You can now log in.');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        toast.error(detail.map(d => d.msg).join(', '));
      } else {
        toast.error(detail || 'Failed to activate account');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="auth-page min-h-screen flex justify-center items-center bg-gray-50">Loading...</div>;
  if (error) return <div className="auth-page min-h-screen flex flex-col items-center justify-center bg-gray-50 text-red-600">{error}</div>;

  return (
    <div className="auth-page flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <div className="bg-white rounded-lg shadow p-8 w-full max-w-md">
      <div className="flex flex-col items-center justify-center h-20 border-b border-secondary-200 bg-white">
          <img src={Logo} alt="Tick Sync Logo" className="h-16 sm:h-20 w-auto max-w-[160px] object-contain my-1" />
        </div>    
            <h2 className="text-2xl font-bold mb-2 text-center">Accept Invitation</h2>
        <p className="mb-6 text-center text-secondary-600">Set your name and password to activate your account.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Full Name</label>
            <input
              type="text"
              name="full_name"
              value={form.full_name}
              onChange={handleChange}
              className="w-full border border-gray-300 rounded px-3 py-2 !bg-white !text-gray-900 focus:outline-none focus:ring focus:border-primary-500"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Username</label>
            <input
              type="text"
              name="username"
              value={form.username}
              onChange={handleChange}
              className={`w-full border border-gray-300 rounded px-3 py-2 !bg-white !text-gray-900 focus:outline-none focus:ring focus:border-primary-500 ${!usernameAvailable && form.username ? 'border-red-500' : ''}`}
              required
              autoComplete="off"
            />
            {form.username && (
              <div className={`text-xs mt-1 ${usernameAvailable ? 'text-green-600' : 'text-red-600'}`}>{checkingUsername ? 'Checking...' : usernameAvailable ? 'Username available' : 'Username is taken'}</div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <div className="relative flex items-center">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={form.password}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-3 py-2 !bg-white !text-gray-900 focus:outline-none focus:ring focus:border-primary-500 pr-24"
                required
              />
              <button
                type="button"
                className="absolute right-20 text-xs text-primary-600 hover:underline"
                onClick={handleSuggestPassword}
                tabIndex={-1}
              >
                Suggest Password
              </button>
              <button
                type="button"
                className="absolute right-3 text-xs text-primary-600 hover:underline"
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Confirm Password</label>
            <div className="relative flex items-center">
              <input
                type={showConfirm ? 'text' : 'password'}
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded px-3 py-2 !bg-white !text-gray-900 focus:outline-none focus:ring focus:border-primary-500 pr-16"
                required
              />
              <button
                type="button"
                className="absolute right-3 text-xs text-primary-600 hover:underline"
                onClick={() => setShowConfirm(v => !v)}
                tabIndex={-1}
              >
                {showConfirm ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
          <button
            type="submit"
            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-2 rounded transition"
            disabled={loading}
          >
            {loading ? 'Activating...' : 'Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite; 